import config from 'config';

import { post } from '@/helper/request';

/** Set by the AI connector's OAuth server (the public API edge) when it sends a member here to sign in. */
export const MCP_RETURN_PARAM = 'mcpReturnTo';

const CALLBACK_PATH = '/productivity/oauth/callback';
const DEFAULT_EDGE_HOSTS = ['api.use1.lyearn.com', 'dev-api.lyearn.com'];
const REQUEST_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const STORAGE_KEY = 'auth:mcpReturnTo';
// Matches how long the edge keeps the sign-in open, so a remembered return never outlives it by much.
const REMEMBER_MS = 30 * 60 * 1000;

const edgeHosts = (): string[] => {
  const configured = config.mcp_edge_hosts;
  return configured
    ? configured
        .split(',')
        .map((host) => host.trim())
        .filter(Boolean)
    : DEFAULT_EDGE_HOSTS;
};

/**
 * The edge callback to return the member to, or null. Only our edge's https callback, carrying a request id, is
 * accepted, so the parameter can never send a signed-in member, with a hand-off code, anywhere else.
 */
export const parseMcpReturnTo = (value?: unknown): URL | null => {
  if (typeof value !== 'string' || !value) {
    return null;
  }
  try {
    const url = new URL(value);
    const allowed =
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      url.pathname === CALLBACK_PATH &&
      edgeHosts().includes(url.host) &&
      REQUEST_PATTERN.test(url.searchParams.get('request') ?? '');
    return allowed ? url : null;
  } catch {
    return null;
  }
};

// Per tab, so a sign-in started by an AI connector never hands back a later, unrelated sign-in in another tab.
const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const remember = (returnTo: URL): void => {
  try {
    storage()?.setItem(
      STORAGE_KEY,
      JSON.stringify({ url: returnTo.toString(), expiresAt: Date.now() + REMEMBER_MS }),
    );
  } catch {
    // storage can be full or disabled; the URL parameter still works on its own
  }
};

const recall = (): URL | null => {
  try {
    const stored = JSON.parse(storage()?.getItem(STORAGE_KEY) ?? 'null');
    if (typeof stored?.expiresAt === 'number' && stored.expiresAt > Date.now()) {
      return parseMcpReturnTo(stored.url);
    }
    forgetMcpReturnTo();
  } catch {
    forgetMcpReturnTo();
  }
  return null;
};

export const forgetMcpReturnTo = (): void => {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // nothing to forget
  }
};

/**
 * Where to hand a member back to, if an AI connector sent them here: from the URL, or remembered from it for a
 * while in this tab, so detours that lose the URL (forgot password, SSO, an expired session) still end with the
 * connector.
 */
export const currentMcpReturnTo = (paramValue?: unknown): URL | null => {
  const fromUrl = parseMcpReturnTo(paramValue);
  if (fromUrl) {
    remember(fromUrl);
    return fromUrl;
  }
  return recall();
};

let leaving: Promise<void> | null = null;

if (typeof window !== 'undefined') {
  // Coming back to this page from the back-forward cache: the hand-off it left for is over.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      leaving = null;
    }
  });
}

/**
 * Hands the signed-in member back to the AI connector, always leaving for the edge callback: with a single-use
 * hand-off code, with `error=access_denied` when this account cannot connect, or with `error=temporarily_unavailable`
 * so the edge can offer to continue. Callers racing after sign-in share one request.
 */
export const completeMcpHandoff = (returnTo: URL): Promise<void> => {
  if (!leaving) {
    leaving = requestHandoff(returnTo).then((target) => {
      forgetMcpReturnTo();
      window.location.assign(target.toString());
    });
  }
  return leaving;
};

const requestHandoff = async (returnTo: URL): Promise<URL> => {
  const target = new URL(returnTo.toString());
  target.searchParams.delete('handoff');
  target.searchParams.delete('error');
  try {
    const response = await post('/api/auth/mcp/handoff', {
      request: returnTo.searchParams.get('request'),
    });
    if (response?.success && typeof response.code === 'string') {
      target.searchParams.set('handoff', response.code);
    } else if (response?.errorCode === 'NOT_ELIGIBLE') {
      target.searchParams.set('error', 'access_denied');
    } else {
      target.searchParams.set('error', 'temporarily_unavailable');
    }
  } catch {
    target.searchParams.set('error', 'temporarily_unavailable');
  }
  return target;
};
